import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * FileUpload — hidden `<input type="file">` primitive.
 *
 * Replaces the raw `<input type="file" hidden onChange={...}>` + trigger-
 * button pattern that previously lived in every page that needed a file
 * picker. The native input stays hidden; consumers render their own
 * trigger UI (typically a <Button>) and call `ref.current?.click()` to
 * open the picker.
 *
 * Why a primitive
 *   - Single place to apply accessibility attributes (hidden but
 *     focusable for screen readers, aria-label support).
 *   - Auto-clears the input value after change so the same file can be
 *     re-selected.
 *   - Single API for both single-file and multi-file modes.
 *   - Eliminates the type="file" exception in the design-system lint:
 *     pages stop rendering raw `<input>` entirely.
 *
 * Usage
 *   const w9Ref = useRef<HTMLInputElement>(null);
 *
 *   <FileUpload
 *     ref={w9Ref}
 *     accept=".pdf,.png,.jpg,.jpeg"
 *     onFile={(file) => handleW9Upload(file)}
 *   />
 *   <Button onClick={() => w9Ref.current?.click()}>
 *     <Upload className="h-4 w-4 mr-1" /> Upload W9
 *   </Button>
 *
 *   // Multi-file:
 *   <FileUpload
 *     ref={ref}
 *     accept="image/*"
 *     multiple
 *     onFiles={(files) => handleBatch(files)}
 *   />
 *
 * The component renders nothing visible — it is purely an imperative
 * file picker. Render your own trigger.
 */

export interface FileUploadProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'type' | 'onChange' | 'value' | 'defaultValue'
  > {
  /**
   * MIME types or file-extension list, e.g. `.pdf,.png,.jpg` or
   * `image/*` or `application/pdf`. Mirrors the native `accept` attr.
   */
  accept?: string;
  /** Allow multiple file selection. */
  multiple?: boolean;
  /**
   * Single-file callback. Fires only when `multiple` is false (or omitted)
   * and at least one file was selected. Receives the first file.
   */
  onFile?: (file: File) => void;
  /**
   * Multi-file callback. Fires only when `multiple` is true. Receives
   * an array of selected Files.
   */
  onFiles?: (files: File[]) => void;
}

export const FileUpload = React.forwardRef<HTMLInputElement, FileUploadProps>(
  ({ accept, multiple = false, onFile, onFiles, className, ...rest }, ref) => {
    return (
      <input
        ref={ref}
        type="file"
        accept={accept}
        multiple={multiple}
        className={cn('sr-only', className)}
        onChange={(e) => {
          const files = e.target.files;
          if (!files || files.length === 0) return;
          if (multiple) {
            onFiles?.(Array.from(files));
          } else {
            onFile?.(files[0]);
          }
          // Allow re-selecting the same file next time.
          e.currentTarget.value = '';
        }}
        {...rest}
      />
    );
  },
);
FileUpload.displayName = 'FileUpload';
